// Per-cycle overrides (admin only, handoff Section 9.1): mode, automatic
// reminders, edit re-auth, window dates, BCC, status, the assessment
// instrument (portal decides; Zoho is mirrored), plus the manual free-text
// purge. A cycle inherits the module defaults unless overridden.

import { useEffect, useState, type JSX } from 'react';
import {
  healthApi,
  HealthApiError,
  type CycleDetail,
  type CycleSettingsPatch,
  type CycleStatus,
  type InstrumentVersionsResponse,
  type RawRevealRow,
} from './api';
import { roleLabel, formatNzDateShort } from './format';
import { OpenLinkPurgeCard } from './OpenLinkPurgeCard';

const PURGE_LOCKED_MESSAGE =
  'The written answers are the debrief material, so purging unlocks once the debrief is marked.';

/** ISO string -> value for <input type="datetime-local"> (local time). */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(value: string): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

export function CycleSettingsTab({
  detail,
  reload,
}: {
  detail: CycleDetail;
  reload: () => void;
}): JSX.Element {
  const { cycle } = detail;
  const [mode, setMode] = useState(cycle.mode);
  const [autoReminders, setAutoReminders] = useState(cycle.auto_reminders);
  const [editReauth, setEditReauth] = useState(cycle.edit_reauth);
  const [status, setStatus] = useState<CycleStatus>(cycle.status);
  const [opensAt, setOpensAt] = useState(isoToLocalInput(cycle.opens_at));
  const [closesAt, setClosesAt] = useState(isoToLocalInput(cycle.closes_at));
  const [bcc, setBcc] = useState(cycle.bcc_emails ?? '');
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Break-glass identity reveal (admin only, audit-logged server-side).
  const [reveal, setReveal] = useState<RawRevealRow[] | null>(null);
  const [revealCaption, setRevealCaption] = useState('');
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);

  // Assessment instrument (the portal is the deciding surface; the Zoho
  // record is updated to match). Changeable only before anyone submits.
  const [catalogue, setCatalogue] = useState<InstrumentVersionsResponse | null>(null);
  const [instFamilyId, setInstFamilyId] = useState('');
  const [instVersion, setInstVersion] = useState(cycle.instrument_version);
  const [instBusy, setInstBusy] = useState(false);
  const instrumentLocked = cycle.status !== 'open' || detail.participation.submitted > 0;

  useEffect(() => {
    healthApi
      .instrumentVersions()
      .then((v) => {
        setCatalogue(v);
        const owner = v.families.find((f) =>
          f.versions.some((ver) => ver.version === cycle.instrument_version),
        );
        setInstFamilyId(owner?.id ?? v.default_family_id);
      })
      .catch(() => {
        /* the card simply shows the pinned version without a picker */
      });
  }, [cycle.instrument_version]);

  const instFamily = catalogue?.families.find((f) => f.id === instFamilyId) ?? null;

  const changeInstrument = (): void => {
    if (!instVersion || instVersion === cycle.instrument_version) return;
    const sure = window.confirm(
      `Change this assessment's instrument to ${instVersion}?\n\n` +
        'Anyone who has already opened the survey or saved a draft will see the new questions ' +
        'on their next visit, and draft answers to questions not in the new instrument will be ' +
        'lost. The Zoho record will be updated to match.',
    );
    if (!sure) return;
    setInstBusy(true);
    setNotice(null);
    healthApi
      .setCycleInstrument(cycle.id, instVersion)
      .then((res) => {
        setNotice(
          res.zoho_synced === true
            ? `Instrument changed to ${res.version}. The Zoho record was updated to match.`
            : res.zoho_synced === false
              ? `Instrument changed to ${res.version}, but the Zoho record could not be updated — it still shows the previous version.`
              : `Instrument changed to ${res.version}. This assessment has no linked Zoho record.`,
        );
        reload();
      })
      .catch((err: unknown) => {
        const code = err instanceof HealthApiError ? err.code : null;
        setNotice(
          code === 'has_responses'
            ? 'The instrument can no longer be changed — a response has been submitted.'
            : code === 'not_open'
              ? 'The instrument can only be changed while the cycle is open.'
              : 'The instrument could not be changed. Please try again.',
        );
      })
      .finally(() => setInstBusy(false));
  };

  const save = (): void => {
    const patch: CycleSettingsPatch = {
      mode,
      auto_reminders: autoReminders,
      edit_reauth: editReauth,
      opens_at: localInputToIso(opensAt),
      closes_at: localInputToIso(closesAt),
      bcc_emails: bcc,
    };
    if (status !== cycle.status) patch.status = status;
    setSaving(true);
    setNotice(null);
    healthApi
      .patchCycleSettings(cycle.id, patch)
      .then(() => {
        setNotice('Cycle settings saved.');
        reload();
      })
      .catch(() => setNotice('The settings could not be saved. Please try again.'))
      .finally(() => setSaving(false));
  };

  // The purge unlocks once the debrief is marked on the saved cycle status
  // (the worker gates on the same rule and answers 409 debrief_not_marked).
  const purgeUnlocked = cycle.status === 'debriefed' || cycle.status === 'archived';

  const purge = (): void => {
    const sure = window.confirm(
      `Permanently remove all written (free-text) answers for ${cycle.church_name}? ` +
        'The 1 to 4 answers are kept. This cannot be undone.',
    );
    if (!sure) return;
    setPurging(true);
    setNotice(null);
    healthApi
      .purgeText(cycle.id)
      .then((res) =>
        setNotice(
          `Written answers purged (${res.purged} entries removed). The 1 to 4 answers are kept.`,
        ),
      )
      .catch((err: unknown) =>
        setNotice(
          err instanceof HealthApiError && err.code === 'debrief_not_marked'
            ? PURGE_LOCKED_MESSAGE
            : 'The purge could not be completed. Please try again.',
        ),
      )
      .finally(() => setPurging(false));
  };

  const doReveal = (): void => {
    const sure = window.confirm(
      'Reveal who submitted for this assessment? Responses stay confidential — this shows only ' +
        'the fact and time of each submission, mapped to the people you invited. This access is ' +
        'recorded in the audit log.',
    );
    if (!sure) return;
    setRevealing(true);
    setRevealError(null);
    healthApi
      .rawReveal(cycle.id)
      .then((res) => {
        setReveal(res.responses);
        setRevealCaption(res.caption);
      })
      .catch(() => setRevealError('The identities could not be revealed. Please try again.'))
      .finally(() => setRevealing(false));
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16, maxWidth: 720 }}>
      {notice && (
        <div className="health-banner health-banner-blue" role="status">
          {notice}
        </div>
      )}

      <div className="card">
        <h3 className="health-section-heading">
          <span>Cycle</span>
        </h3>
        <div className="health-field-grid">
          <div className="health-field">
            <label className="health-input-label" htmlFor="cycle-mode">
              Mode
            </label>
            <select
              id="cycle-mode"
              className="health-input"
              value={mode}
              onChange={(e) => setMode(e.target.value === 'open' ? 'open' : 'rostered')}
            >
              <option value="rostered">Rostered (named invite list, reminders)</option>
              <option value="open">Open link (shared link, no roster)</option>
            </select>
          </div>
          <div className="health-field">
            <label className="health-input-label" htmlFor="cycle-status">
              Status
            </label>
            <select
              id="cycle-status"
              className="health-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as CycleStatus)}
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="debriefed">Debriefed</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <h3 className="health-section-heading" style={{ marginTop: 22 }}>
          <span>Window</span>
        </h3>
        <div className="health-field-grid">
          <div className="health-field">
            <label className="health-input-label" htmlFor="cycle-opens">
              Opens
            </label>
            <input
              id="cycle-opens"
              type="datetime-local"
              className="health-input"
              value={opensAt}
              onChange={(e) => setOpensAt(e.target.value)}
            />
          </div>
          <div className="health-field">
            <label className="health-input-label" htmlFor="cycle-closes">
              Closes
            </label>
            <input
              id="cycle-closes"
              type="datetime-local"
              className="health-input"
              value={closesAt}
              onChange={(e) => setClosesAt(e.target.value)}
            />
          </div>
        </div>

        <h3 className="health-section-heading" style={{ marginTop: 22 }}>
          <span>Reminders and access</span>
        </h3>
        <div className="health-field-grid">
          <div className="health-field">
            <label className="health-input-label" htmlFor="cycle-auto-reminders">
              Automatic reminders
            </label>
            <select
              id="cycle-auto-reminders"
              className="health-input"
              value={autoReminders}
              onChange={(e) => setAutoReminders(e.target.value === 'on' ? 'on' : 'off')}
            >
              <option value="off">Off (manual reminders only)</option>
              <option value="on">On (nudge non-responders before close)</option>
            </select>
          </div>
          <div className="health-field">
            <label className="health-input-label" htmlFor="cycle-edit-reauth">
              Edit re-authentication
            </label>
            <select
              id="cycle-edit-reauth"
              className="health-input"
              value={editReauth}
              onChange={(e) => setEditReauth(e.target.value === 'on' ? 'on' : 'off')}
            >
              <option value="off">Off (the link alone allows editing)</option>
              <option value="on">On (a PIN is emailed before editing)</option>
            </select>
          </div>
        </div>

        <h3 className="health-section-heading" style={{ marginTop: 22 }}>
          <span>Email</span>
        </h3>
        <div className="health-field-grid">
          <div className="health-field health-field-wide">
            <label className="health-input-label" htmlFor="cycle-bcc">
              BCC on cycle emails (comma-separated, optional)
            </label>
            <input
              id="cycle-bcc"
              type="text"
              className="health-input"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              placeholder="facilitator@example.org"
            />
          </div>
        </div>

        <div className="health-form-actions">
          <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
            {saving ? 'Saving...' : 'Save cycle settings'}
          </button>
        </div>
      </div>

      <div className="card">
        <h3 className="health-section-heading">
          <span>Assessment instrument</span>
        </h3>
        <p style={{ fontSize: 13, color: 'var(--health-grey)', lineHeight: 1.7, marginBottom: 12 }}>
          This assessment runs on <strong>{instFamily?.name ?? 'instrument'}</strong> version{' '}
          <strong>{cycle.instrument_version}</strong>. The instrument can be changed here while the
          cycle is open and before anyone has submitted; the Zoho record is updated to match.
        </p>
        {catalogue && !instrumentLocked && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              className="health-input"
              style={{ width: 'auto', maxWidth: 240 }}
              aria-label="Instrument"
              value={instFamilyId}
              onChange={(e) => {
                const fam = catalogue.families.find((f) => f.id === e.target.value);
                setInstFamilyId(e.target.value);
                // Default to the family's pinned-or-latest version.
                setInstVersion(
                  fam?.versions.some((v) => v.version === cycle.instrument_version)
                    ? cycle.instrument_version
                    : (fam?.versions.find((v) => v.is_default)?.version ??
                        fam?.versions[fam.versions.length - 1]?.version ??
                        ''),
                );
              }}
            >
              {catalogue.families
                .filter((f) => f.versions.length > 0)
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
            </select>
            <select
              className="health-input"
              style={{ width: 'auto' }}
              aria-label="Instrument version"
              value={instVersion}
              onChange={(e) => setInstVersion(e.target.value)}
            >
              {(instFamily?.versions ?? []).map((v) => (
                <option key={v.version} value={v.version}>
                  {v.version}
                  {v.version === cycle.instrument_version ? ' (current)' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-sm"
              data-tip="Re-pins this assessment to the chosen version and updates the Zoho record to match"
              disabled={instBusy || !instVersion || instVersion === cycle.instrument_version}
              onClick={changeInstrument}
            >
              {instBusy ? 'Changing...' : 'Change instrument'}
            </button>
          </div>
        )}
        {instrumentLocked && (
          <p style={{ fontSize: 12.5, color: 'var(--health-grey)', margin: 0 }}>
            {cycle.status !== 'open'
              ? 'The cycle is no longer open, so its instrument is fixed.'
              : 'A response has been submitted, so the instrument is now fixed for this assessment.'}
          </p>
        )}
      </div>

      <div className="card" style={{ borderLeft: '4px solid var(--band-priority)' }}>
        <h3 style={{ fontSize: 15, marginBottom: 6 }}>Purge written answers</h3>
        <p style={{ fontSize: 13, color: 'var(--health-grey)', lineHeight: 1.7, marginBottom: 12 }}>
          Written (free-text) answers are the sensitive part of an assessment. Because they are the
          material the debrief works from, they can only be purged once the cycle is marked
          debriefed. Any that remain are removed automatically six months after the cycle closes.
          The 1 to 4 answers are always kept. This cannot be undone.
        </p>
        <button
          type="button"
          className="btn btn-danger btn-sm"
          disabled={purging || !purgeUnlocked}
          title={purgeUnlocked ? undefined : PURGE_LOCKED_MESSAGE}
          onClick={purge}
        >
          {purging ? 'Purging...' : 'Purge written answers now'}
        </button>
        {!purgeUnlocked && (
          <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginTop: 8 }}>
            {PURGE_LOCKED_MESSAGE}
          </p>
        )}
      </div>

      {cycle.mode === 'open' && <OpenLinkPurgeCard cycleId={cycle.id} reload={reload} />}

      <div className="card" style={{ borderLeft: '4px solid var(--band-priority)' }}>
        <h3 style={{ fontSize: 15, marginBottom: 6 }}>Reveal respondent identities</h3>
        <p style={{ fontSize: 13, color: 'var(--health-grey)', lineHeight: 1.7, marginBottom: 12 }}>
          Assessment responses are anonymous by design. This break-glass view maps the people you
          invited to the fact and time they submitted — never to their individual answers. It is
          here for administration only: confirming who has responded and reconciling the roster.
          Every reveal is written to the audit log.
        </p>
        {revealError && (
          <p style={{ fontSize: 13, color: 'var(--band-priority)', marginBottom: 10 }}>
            {revealError}
          </p>
        )}
        {reveal === null ? (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            disabled={revealing || detail.participation.submitted === 0}
            title={
              detail.participation.submitted === 0
                ? 'No responses have been submitted yet'
                : undefined
            }
            onClick={doReveal}
          >
            {revealing ? 'Revealing...' : 'Reveal identities (logged)'}
          </button>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Role</th>
                    <th scope="col">Submitted</th>
                    <th scope="col">Edits</th>
                  </tr>
                </thead>
                <tbody>
                  {reveal.map((r) => (
                    <tr key={r.response_id}>
                      <td>
                        {r.display_name || <span style={{ color: 'var(--health-grey)' }}>—</span>}
                      </td>
                      <td>{r.email || <span style={{ color: 'var(--health-grey)' }}>—</span>}</td>
                      <td>{roleLabel(r.role)}</td>
                      <td>{formatNzDateShort(r.submitted_at)}</td>
                      <td>{r.revision > 1 ? `Edited ${r.revision - 1}×` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginTop: 10 }}>
              {revealCaption}
            </p>
            <button
              type="button"
              className="btn btn-sm"
              style={{ marginTop: 4 }}
              onClick={() => setReveal(null)}
            >
              Hide
            </button>
          </>
        )}
      </div>
    </div>
  );
}
